import React, { useState, useMemo, useEffect, useCallback } from 'react'
import { useParams, useSearchParams, useNavigate, useLocation } from 'react-router-dom'
import { 
  ArrowLeft, 
  Download, 
  Image, 
  Loader2, 
  AlertCircle, 
  CheckSquare, 
  Square, 
  Eye,
  Filter,
  Grid3X3,
  Settings,
  RefreshCw,
  Heart
} from 'lucide-react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { 
  getImages, 
  getSampleImages, 
  getSmartImages, 
  getLocalImageLibrary, 
  addImageFavorite,
  removeImageFavorite,
  checkFavoriteStatus,
  clearImagesCache,
  clearImageFilesCache,
  request,
  type ImageItem, 
  type ImagesResponse 
} from '../api/fancaps'
import { getStoredToken } from '../utils/storage'
import RateLimitWarning from '../components/RateLimitWarning'
import AuthenticatedImage from '../components/AuthenticatedImage'

// 生成有效的文件名
const generateValidFilename = (title: string): string => {
  if (!title || title.trim() === '') {
    return 'fancaps_images'
  }
  
  // 移除或替换特殊字符，保留中文、英文、数字
  let filename = title
    .replace(/[<>:"/\\|?*]/g, '') // 移除文件系统禁用字符
    .replace(/[\s\-_]+/g, '_') // 多个空格、横线、下划线替换为单个下划线
    .replace(/^_+|_+$/g, '') // 移除开头和结尾的下划线
  
  // 如果处理后为空或全是下划线，使用默认名称
  if (!filename || /^_*$/.test(filename)) {
    filename = 'fancaps_images'
  }
  
  // 长度限制，避免过长的文件名
  if (filename.length > 50) {
    filename = filename.substring(0, 50).replace(/_+$/, '')
  }
  
  return filename
}

// 添加API函数来获取指定范围的图片
const getRangeImages = async (startId: string, endId: string): Promise<ImagesResponse> => {
  return await request<ImagesResponse>(`/images/range/${startId}/${endId}`)
}

const ImagesPage: React.FC = () => {
  const { episodeId, startId, endId } = useParams<{ episodeId: string; startId: string; endId: string }>()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient() // 添加 queryClient 定义
  
  // 检测页面模式
  const isSampleMode = location.pathname === '/sample-images'
  const isRangeMode = location.pathname.startsWith('/images-range/')
  const isLocalLibraryMode = location.pathname === '/local-library'
  
  const initialTitle = isSampleMode 
    ? '样本图片（基于实战ID范围）' 
    : isRangeMode 
      ? (searchParams.get('title') || `图片范围 ${startId}-${endId}`)
      : isLocalLibraryMode
        ? '本地图片库（避免重复请求）'
        : (searchParams.get('title') || '加载中...')
  
  // 状态管理
  const [title, setTitle] = useState(initialTitle)
  const [selectedImages, setSelectedImages] = useState<Set<string>>(new Set())
  const [gridColumns, setGridColumns] = useState(6) // 网格列数
  const [displayedCount, setDisplayedCount] = useState(50) // 当前显示数量
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [sortBy, setSortBy] = useState('date_desc') // 排序方式，默认日期降序
  
  // 添加图片尺寸选择状态
  const [downloadImageSize, setDownloadImageSize] = useState<'small' | 'large'>('large')
  
  // 清除缓存状态
  const [isClearingCache, setIsClearingCache] = useState(false)

  // 🆕 图片获取模式状态
  const [imageMode, setImageMode] = useState<'smart' | 'fast'>('smart')

  // 获取图片数据
  const {
    data: imagesData,
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: (isSampleMode 
      ? ['sample-images'] 
      : isRangeMode 
        ? ['range-images', startId, endId]
        : isLocalLibraryMode
          ? ['local-library', sortBy]  // 只有本地图片库需要sortBy
          : ['images', episodeId, imageMode, 'v2']) as readonly unknown[], // 🔧 添加版本号强制刷新缓存
    queryFn: async (): Promise<ImagesResponse> => {
      if (isSampleMode) {
        return await getSampleImages(10000) // 获取10000张样本图片
      } else if (isRangeMode) {
        if (!startId || !endId) throw new Error('缺少起始或结束ID')
        return await getRangeImages(startId, endId)
      } else if (isLocalLibraryMode) {
        console.log('🔍 正在获取本地图片库数据...')
        const result = await getLocalImageLibrary(1, 10000, sortBy) // 增加排序参数
        console.log('✅ 本地图片库数据获取成功:', {
          总数: result.total,
          当前页图片数: result.images.length,
          图片ID示例: result.images.slice(0, 5).map(img => img.id),
          缓存状态: result.cached,
          排序方式: sortBy
        })
        return result
      } else {
        if (!episodeId) throw new Error('缺少剧集ID')
        
        // 🆕 根据模式选择不同的API端点
        if (imageMode === 'fast') {
          // 快速模式：标准分页，图片较少但速度快
          return await request<ImagesResponse>(`/images/episode/${encodeURIComponent(episodeId)}/fast`)
        } else {
          // 智能模式：使用新的探测式分页API（直接调用/episode/{id}，不使用有问题的smart API）
          return await request<ImagesResponse>(`/images/episode/${encodeURIComponent(episodeId)}`)
        }
      }
    },
    enabled: isSampleMode || isRangeMode || isLocalLibraryMode || !!episodeId,
    retry: 2,
    staleTime: 60 * 60 * 1000, // 🚀 增加到60分钟缓存 - 图片列表基本不变
    gcTime: 24 * 60 * 60 * 1000, // 🚀 垃圾回收时间24小时 - 保持更久的本地缓存
  })

  // 添加本地图片库专用的调试信息
  React.useEffect(() => {
    if (isLocalLibraryMode && imagesData) {
      console.log('📊 本地图片库渲染数据:', {
        模式: '本地图片库',
        总图片数: imagesData.total,
        当前显示: imagesData.images.length,
        标题: imagesData.episode_title,
        缓存状态: imagesData.cached,
        样本图片URL: imagesData.images.slice(0, 3).map(img => getImageProxyUrl(img))
      })
    }
  }, [isLocalLibraryMode, imagesData])
  
  // 更新搜索历史中的图片数据
  React.useEffect(() => {
    const updateSearchHistory = async () => {
      // 检查是否来自搜索历史，且有实际的图片数据
      if (episodeId && imagesData?.images && imagesData.images.length > 0) {
        const searchParams = new URLSearchParams(window.location.search)
        const searchTerm = searchParams.get('searchTerm')
        
        // 🎯 修复：使用正确的URL参数
        const workTitle = searchParams.get('workTitle') // 作品标题
        const episodeTitle = searchParams.get('episodeTitle') || searchParams.get('title') || title // 集数标题
        
        if (searchTerm) {
          try {
            // 获取实际的图片ID列表
            const imageIds = imagesData.images.map(img => img.id)
            
            // 🎯 修复：如果URL参数中有workTitle，直接使用；否则从episodeId解析
            let showTitle = workTitle || "Unknown Show"
            if (!workTitle && episodeId.includes('-')) {
              // 格式如 "3927-Clannad/Episode_9__Until_the_End_of_the_Dream"
              const parts = episodeId.split('-')
              if (parts.length >= 2) {
                showTitle = parts[1].split('/')[0] // 取Clannad部分
              }
            }
            
            // 调用新的record-view接口
            try {
              await request('/images/record-view', {
                method: 'POST',
                body: JSON.stringify({
                  search_term: searchTerm,
                  show_title: showTitle,
                  episode_title: episodeTitle,
                  episode_id: episodeId,
                  image_ids: imageIds
                })
              })
              console.log('✅ 新版浏览记录保存成功', {
                剧集ID: episodeId,
                搜索词: searchTerm,
                作品标题: showTitle,
                剧集标题: episodeTitle,
                图片数量: imageIds.length,
                图片示例: imageIds.slice(0, 5)
              })
            } catch (recordError) {
              console.warn('⚠️ 新版浏览记录保存失败', recordError)
            }
          } catch (error) {
            console.warn('⚠️ 更新浏览记录时出错:', error)
          }
        }
      }
    }
    
    updateSearchHistory()
  }, [episodeId, imagesData, title])

  // 当API数据加载后，更新标题
  useEffect(() => {
    if (imagesData?.episode_title && !searchParams.get('title') && !isSampleMode && !isRangeMode && !isLocalLibraryMode) {
      setTitle(imagesData.episode_title)
    }
  }, [imagesData?.episode_title, searchParams, isSampleMode, isRangeMode, isLocalLibraryMode])

  // 过滤和分页的图片列表
  const filteredImages = useMemo(() => {
    if (!imagesData?.images) return []
    
    let filtered = imagesData.images
    
    // 搜索过滤
    if (searchTerm.trim()) {
      filtered = filtered.filter(img => 
        img.id.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }
    
    return filtered
  }, [imagesData?.images, searchTerm])

  const displayedImages = useMemo(() => {
    return filteredImages.slice(0, displayedCount)
  }, [filteredImages, displayedCount])

  // 选择相关的处理函数
  const handleImageSelect = useCallback((imageId: string, selected: boolean) => {
    setSelectedImages(prev => {
      const newSet = new Set(prev)
      if (selected) {
        newSet.add(imageId)
      } else {
        newSet.delete(imageId)
      }
      return newSet
    })
  }, [])

  const handleSelectAll = useCallback(() => {
    if (selectedImages.size === displayedImages.length) {
      // 取消全选
      setSelectedImages(new Set())
    } else {
      // 全选当前显示的图片
      setSelectedImages(new Set(displayedImages.map(img => img.id)))
    }
  }, [selectedImages.size, displayedImages])

  // 无限滚动加载更多
  const loadMore = useCallback(() => {
    if (isLoadingMore || displayedCount >= filteredImages.length) return
    
    setIsLoadingMore(true)
    setTimeout(() => {
      setDisplayedCount(prev => Math.min(prev + 50, filteredImages.length))
      setIsLoadingMore(false)
    }, 300)
  }, [isLoadingMore, displayedCount, filteredImages.length])

  // 滚动事件监听
  useEffect(() => {
    const handleScroll = () => {
      if (
        window.innerHeight + document.documentElement.scrollTop
        >= document.documentElement.offsetHeight - 1000
      ) {
        loadMore()
      }
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [loadMore])

  const handleBackClick = () => {
    navigate(-1)
  }

  const handleDownload = async () => {
    if (selectedImages.size === 0) {
      alert('请先选择要下载的图片')
      return
    }
    
    try {
      // 构建参数化代理URL列表（不影响其他地方的小图显示）
      const sizeParam = downloadImageSize === 'small' ? 'small' : 'large'
      const imageUrls = Array.from(selectedImages).map(imageId => {
        return `/api/images/${imageId}.jpg?size=${sizeParam}`
      })
      
      // 生成文件名前缀，包含尺寸信息
      const sizeLabel = downloadImageSize === 'small' ? '小图' : '大图'
      const filenamePrefix = `${generateValidFilename(title)}_${sizeLabel}`
      
      // 调用下载API - 使用正确的下载端点
      const downloadData = await request<{download_id: string, file_count: number}>('/download', {
        method: 'POST',
        body: JSON.stringify({
          image_urls: imageUrls,
          filename_prefix: filenamePrefix
        }),
      })
      
      // 显示下载信息，包含尺寸说明
      const sizeDescription = downloadImageSize === 'small' 
        ? '📱 小图模式 - 快速下载，适合预览'
        : '🖼️ 大图模式 - 高清原图，适合收藏'
      
      alert(`下载任务已创建！\n${sizeDescription}\n任务ID: ${downloadData.download_id}\n将下载 ${downloadData.total_images} 张图片\n\n页面将在几秒后自动跳转到下载链接...`)
      
      // 轮询检查下载状态
      const checkDownloadStatus = async () => {
        try {
          const status = await request<{status: string, error?: string}>(`/download/${downloadData.download_id}/status`)
          console.log('🚨 DEBUG: 状态查询结果:', status)
            
          if (status.status === 'completed') {
            // 下载完成，使用带认证的fetch请求下载文件
            try {
              const response = await fetch(`/api/download/${downloadData.download_id}/file`, {
                method: 'GET',
                headers: {
                  'Authorization': `Bearer ${getStoredToken()}`,
                }
              })
              
              if (!response.ok) {
                throw new Error(`下载失败: ${response.status} ${response.statusText}`)
              }
              
              // 获取文件名
              const contentDisposition = response.headers.get('Content-Disposition')
              let filename = 'fancaps_images.zip'
              if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename\*?=([^;]+)/)
                if (filenameMatch) {
                  filename = decodeURIComponent(filenameMatch[1].replace(/UTF-8''/, '').replace(/"/g, ''))
                }
              }
              
              // 创建blob并触发下载
              const blob = await response.blob()
              console.log('📦 下载文件:', filename, `(${Math.round(blob.size/1024)}KB)`)
              
              // 使用更可靠的下载方法，避免popup blocker
              if (window.navigator && (window.navigator as any).msSaveOrOpenBlob) {
                // IE浏览器
                (window.navigator as any).msSaveOrOpenBlob(blob, filename)
              } else {
                // 现代浏览器 - 使用改进的下载方法
                const url = window.URL.createObjectURL(blob)
                
                // 创建下载链接并立即触发
                const link = document.createElement('a')
                link.href = url
                link.download = filename
                link.style.display = 'none'
                
                // 添加到DOM中
                document.body.appendChild(link)
                
                // 立即触发点击，避免异步延迟导致的popup blocking
                try {
                  link.click()
                } catch (clickError) {
                  console.error('❌ 点击下载链接失败:', clickError)
                  // 回退方案：直接打开URL
                  window.open(url, '_blank')
                }
                
                // 清理资源
                setTimeout(() => {
                  document.body.removeChild(link)
                  window.URL.revokeObjectURL(url)
                }, 100)
              }
              
              // 备用下载链接（仅在控制台显示）
              const downloadUrl = `/api/download/${downloadData.download_id}/file`
              console.log('🔗 备用下载链接:', window.location.origin + downloadUrl)
              console.log('💡 如果自动下载失败，可以复制上述链接到浏览器地址栏手动下载')
              
            } catch (error) {
              console.error('❌ 下载文件失败:', error)
              alert(`下载文件失败: ${error.message}\n\n请尝试刷新页面后重新下载`)
            }
            return
          } else if (status.status === 'failed') {
            alert(`下载失败: ${status.error || '未知错误'}`)
            return
          }
          
          // 继续轮询
          setTimeout(checkDownloadStatus, 2000)
        } catch (error) {
          console.error('检查下载状态失败:', error)
          // 轮询失败也继续尝试，避免因网络抖动而中断
          setTimeout(checkDownloadStatus, 3000) // 延长间隔
        }
      }
      
      // 开始轮询
      setTimeout(checkDownloadStatus, 2000)
      
    } catch (error) {
      console.error('下载失败:', error)
      alert(`下载失败: ${error instanceof Error ? error.message : '网络错误'}`)
    }
  }

  const getImageProxyUrl = (image: ImageItem) => {
    // 🎯 修复：使用后端API返回的small_url，包含正确的type参数
    return image.small_url || `/api/images/${image.id}.jpg`
  }

  // 🎯 修复：精确的当前集数缓存清除功能
  const clearCurrentEpisodeCache = async () => {
    if (!episodeId || isSampleMode || isRangeMode || isLocalLibraryMode) {
      console.warn('⚠️ 当前模式不支持缓存清除')
      return
    }

    try {
      const result = await clearImagesCache({ episode_id: episodeId })
      console.log(`✅ 当前集数缓存已清除: ${result.message}, 清除了 ${result.cleared_items} 个缓存项`)
      return result
    } catch (error) {
      console.error('清除当前集数缓存失败:', error)
      throw error
    }
  }

  // 清除缓存功能 - 改进版：更好的用户反馈
  const handleClearCache = async () => {
    if (isClearingCache) return
    
    // 🎯 修复：更明确的确认信息，强调只清除当前集数
    const currentEpisodeTitle = title || episodeId || '当前集数'
    const confirmMessage = `确定要清除当前集数的缓存吗？\n\n集数: ${currentEpisodeTitle}\n\n这将清除：\n• 当前集数的图片列表缓存\n• 本地图片文件缓存\n\n⚠️ 只影响当前集数，其他集数缓存保持不变\n\n清除后会重新从服务器获取数据。`
    
    if (!confirm(confirmMessage)) {
      return
    }

    setIsClearingCache(true)
    
    try {
      // 显示进度提示
      console.log('🧹 开始清除当前集数缓存...')
      
      let cacheResult = null
      let filesResult = null
      
      // 🎯 修复：使用精确的当前集数缓存清除
      if (episodeId && !isSampleMode && !isRangeMode && !isLocalLibraryMode) {
        cacheResult = await clearCurrentEpisodeCache()
        console.log(`✅ 当前集数图片列表缓存已清除: ${cacheResult?.cleared_items || 0} 个缓存项`)
      }
      
      // 🎯 修复：清除当前集数的本地图片文件缓存
      filesResult = await clearImageFilesCache({ episode_id: episodeId })
      console.log(`✅ 当前集数本地图片文件缓存已清除: ${filesResult.deleted_files} 个文件`)
      
      // 🎯 修复：只清除当前集数的查询缓存，不影响其他集数
      if (episodeId && !isSampleMode && !isRangeMode && !isLocalLibraryMode) {
        // 只清除当前集数的具体查询缓存
        queryClient.invalidateQueries({ queryKey: ['images', episodeId, imageMode, 'v2'] })
        console.log(`🔧 已清除当前集数的React Query缓存: episodeId=${episodeId}`)
      } else if (isSampleMode) {
        queryClient.invalidateQueries({ queryKey: ['sample-images'] })
      } else if (isRangeMode) {
        queryClient.invalidateQueries({ queryKey: ['range-images', startId, endId] })
      } else if (isLocalLibraryMode) {
        queryClient.invalidateQueries({ queryKey: ['local-library', sortBy] })
      }
      
      // 手动触发刷新
      await refetch()
      
      // 🎯 修复：更详细的成功反馈
      const successMessage = [
        '✅ 缓存清除成功！',
        '',
        `📺 集数: ${currentEpisodeTitle}`,
        `🗂️ 清除缓存项: ${cacheResult?.cleared_items || 0} 个`,
        `📁 删除文件: ${filesResult?.deleted_files || 0} 个`,
        '',
        '🔄 页面将重新加载最新数据'
      ].join('\n')
      
      alert(successMessage)
      
    } catch (error) {
      console.error('清除缓存失败:', error)
      
      // 🎯 修复：更友好的错误信息
      const errorMessage = (error as any)?.message || '未知错误'
      alert(`❌ 清除缓存失败\n\n错误信息: ${errorMessage}\n\n💡 建议: 请刷新页面后重试，或联系技术支持`)
    } finally {
      setIsClearingCache(false)
    }
  }

  if (error) {
    const errorObj = error as any
    const isRateLimit = errorObj?.message?.includes('429') || errorObj?.message?.includes('rate limit')
    const errorMessage = isRateLimit 
      ? 'IP被临时限制，请稍后重试' 
      : errorObj?.message || '加载失败'

    return (
      <div className="px-6 py-6 relative">
        <div className="flex items-center mb-6">
          <button
            onClick={handleBackClick}
            className="flex items-center text-blue-600 hover:text-blue-700 mr-4"
          >
            <ArrowLeft className="w-5 h-5 mr-1" />
            返回
          </button>
          <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
        </div>
        
        {isRateLimit ? (
          <RateLimitWarning onRetry={() => refetch()} />
        ) : (
          <div className="max-w-2xl mx-auto">
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
              <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-red-800 mb-2">加载失败</h3>
              <p className="text-red-700 mb-4">
                {errorMessage}
              </p>
              <button
                onClick={() => refetch()}
                className="btn btn-outline text-red-700 border-red-300 hover:bg-red-50"
              >
                重试
              </button>
            </div>
          </div>
        )}
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="px-6 py-6 relative">
        <div className="flex items-center mb-6">
          <button
            onClick={handleBackClick}
            className="flex items-center text-blue-600 hover:text-blue-700 mr-4"
          >
            <ArrowLeft className="w-5 h-5 mr-1" />
            返回
          </button>
          <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
        </div>
        
        <div className="max-w-2xl mx-auto text-center py-12">
          <Loader2 className="w-12 h-12 text-blue-500 mx-auto mb-4 animate-spin" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            正在加载图片列表...
          </h3>
          <p className="text-gray-500">
            正在聚合所有截图，请稍候
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="px-6 py-6 relative">
      {/* 页面头部 - 添加吸顶效果 */}
      <div className="sticky top-0 z-50 bg-white border-b border-gray-200 -mx-6 px-6 py-4 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <button
              onClick={handleBackClick}
              className="flex items-center text-blue-600 hover:text-blue-700 mr-4"
            >
              <ArrowLeft className="w-5 h-5 mr-1" />
              返回
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
              <p className="text-sm text-gray-500 mt-1">
                {imagesData?.cached && '(缓存) '}
                共 {imagesData?.total || 0} 张截图
                {filteredImages.length !== imagesData?.total && 
                  ` · 筛选后 ${filteredImages.length} 张`
                }
              </p>
            </div>
          </div>
          
          <button
            onClick={() => refetch()}
            className="btn btn-outline flex items-center"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            刷新
          </button>
        </div>
      </div>

      {/* 工具栏 - 优化布局：分层显示，支持吸顶 */}
      <div className="sticky top-20 z-40 bg-white border-b border-gray-200 -mx-6 px-6 py-4 mb-6">
        <div className="max-w-7xl mx-auto">
          {/* 第一行：主要操作 */}
          <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between mb-4">
            {/* 左侧：模式切换和搜索 */}
            <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
              {/* 🆕 图片获取模式切换 */}
              {!isSampleMode && !isRangeMode && !isLocalLibraryMode && episodeId && (
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium text-gray-700">模式:</label>
                  <select
                    value={imageMode}
                    onChange={(e) => setImageMode(e.target.value as 'smart' | 'fast')}
                    className="text-sm border border-gray-300 rounded-md px-3 py-1 bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="smart">🎯 智能完整</option>
                    <option value="fast">⚡ 快速基础</option>
                  </select>
                </div>
              )}
              
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  placeholder="搜索图片ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="input text-sm w-44"
                />
              </div>
              
              <div className="flex items-center gap-2">
                <button
                  onClick={handleSelectAll}
                  className="btn btn-outline text-sm flex items-center"
                >
                  {selectedImages.size === displayedImages.length && displayedImages.length > 0 ? (
                    <CheckSquare className="w-4 h-4 mr-1" />
                  ) : (
                    <Square className="w-4 h-4 mr-1" />
                  )}
                  {selectedImages.size === displayedImages.length && displayedImages.length > 0 ? '取消全选' : '全选'}
                </button>
                
                <span className="text-sm text-gray-600 font-medium">
                  已选 {selectedImages.size} 张
                </span>
              </div>
            </div>

            {/* 右侧：下载操作 */}
            <div className="flex items-center gap-3">
              <button
                onClick={handleDownload}
                disabled={selectedImages.size === 0}
                className={`btn flex items-center ${
                  selectedImages.size > 0 
                    ? 'btn-primary' 
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                <Download className="w-4 h-4 mr-2" />
                下载选中项 ({selectedImages.size})
              </button>
            </div>
          </div>

          {/* 第二行：显示设置和工具 */}
          <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between pt-3 border-t border-gray-100">
            {/* 左侧：显示设置 */}
            <div className="flex items-center gap-4">
              {/* 本地图片库排序选择器 */}
              {isLocalLibraryMode && (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">排序:</span>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="text-sm border border-gray-300 rounded px-2 py-1 bg-white"
                  >
                    <option value="id_asc">ID 升序</option>
                    <option value="id_desc">ID 降序</option>
                    <option value="date_asc">日期 升序</option>
                    <option value="date_desc">日期 降序</option>
                    <option value="size_asc">大小 升序</option>
                    <option value="size_desc">大小 降序</option>
                  </select>
                </div>
              )}
              
              <div className="flex items-center gap-2">
                <Grid3X3 className="w-4 h-4 text-gray-500" />
                <select
                  value={gridColumns}
                  onChange={(e) => setGridColumns(Number(e.target.value))}
                  className="text-sm border border-gray-300 rounded px-2 py-1 bg-white"
                >
                  <option value={3}>3 列</option>
                  <option value={4}>4 列</option>
                  <option value={5}>5 列</option>
                  <option value={6}>6 列</option>
                  <option value={8}>8 列</option>
                </select>
              </div>

              {/* 简化的图片尺寸选择控件 - 仅在有选中图片时显示 */}
              {selectedImages.size > 0 && (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">下载:</span>
                  <div className="flex bg-gray-100 rounded-lg p-1">
                    <button
                      onClick={() => setDownloadImageSize('small')}
                      className={`px-2 py-1 text-xs rounded-md transition-all ${
                        downloadImageSize === 'small'
                          ? 'bg-blue-500 text-white shadow-sm'
                          : 'text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      小图
                    </button>
                    <button
                      onClick={() => setDownloadImageSize('large')}
                      className={`px-2 py-1 text-xs rounded-md transition-all ${
                        downloadImageSize === 'large'
                          ? 'bg-blue-500 text-white shadow-sm'
                          : 'text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      大图
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* 右侧：工具按钮 */}
            <div className="flex items-center gap-2">
              <button
                onClick={handleClearCache}
                disabled={isClearingCache}
                className={`btn btn-outline btn-sm flex items-center ${
                  isClearingCache 
                    ? 'opacity-50 cursor-not-allowed'
                    : 'text-orange-600 border-orange-300 hover:bg-orange-50'
                }`}
                title="清除图片缓存并重新加载数据"
              >
                {isClearingCache ? (
                  <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                ) : (
                  <RefreshCw className="w-3 h-3 mr-1" />
                )}
                {isClearingCache ? '清除中' : '清除缓存'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 图片网格 */}
      {displayedImages.length > 0 ? (
        <div className="space-y-4">
          <div 
            className="grid gap-4"
            style={{ 
              gridTemplateColumns: `repeat(${gridColumns}, 1fr)` 
            }}
          >
            {displayedImages.map((image, index) => {
              // 从URL参数和标题中解析更准确的信息
              const url = searchParams.get('url') || ''
              const workTitle = searchParams.get('workTitle') || 
                               (title.includes(' - ') ? title.split(' - ')[0] : title)
              const episodeTitle = searchParams.get('episodeTitle') || 
                                  (title.includes(' - ') ? title.split(' - ')[1] : title)
              
              // 生成更准确的ID
              const workId = workTitle.replace(/[^a-zA-Z0-9\u4e00-\u9fa5]/g, '_')
              const episodeIdValue = episodeId || 
                                   episodeTitle.replace(/[^a-zA-Z0-9\u4e00-\u9fa5]/g, '_')
              
              return (
                <ImageCard
                  key={image.id}
                  image={image}
                  isSelected={selectedImages.has(image.id)}
                  onSelect={handleImageSelect}
                  getImageUrl={getImageProxyUrl}
                  index={index}
                  episodeTitle={episodeTitle}
                  workTitle={workTitle}
                  episodeId={episodeIdValue}
                  workId={workId}
                />
              )
            })}
          </div>

          {/* 加载更多指示器 */}
          {displayedCount < filteredImages.length && (
            <div className="text-center py-8">
              {isLoadingMore ? (
                <div className="flex items-center justify-center">
                  <Loader2 className="w-6 h-6 animate-spin text-blue-500 mr-2" />
                  <span className="text-gray-600">加载更多图片...</span>
                </div>
              ) : (
                <div className="text-gray-500">
                  显示 {displayedCount} / {filteredImages.length} 张图片
                  <div className="text-sm mt-1">继续向下滚动查看更多</div>
                </div>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="text-center py-12">
          <div className="text-gray-500 mb-4">
            <Image className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            {searchTerm ? '没有找到匹配的图片' : '暂无图片数据'}
          </div>
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="btn btn-outline"
            >
              清除搜索条件
            </button>
          )}
        </div>
      )}
    </div>
  )
}

// 图片卡片组件
interface ImageCardProps {
  image: ImageItem
  isSelected: boolean
  onSelect: (imageId: string, selected: boolean) => void
  getImageUrl: (image: ImageItem) => string
  index: number
  episodeTitle?: string
  workTitle?: string
  episodeId?: string
  workId?: string
}

const ImageCard: React.FC<ImageCardProps> = ({ 
  image, 
  isSelected, 
  onSelect, 
  getImageUrl,
  index,
  episodeTitle = '未知集数',
  workTitle = '未知作品',
  episodeId = 'unknown',
  workId = 'unknown'
}) => {
  const queryClient = useQueryClient()
  const [imageLoaded, setImageLoaded] = useState(false)
  const [imageError, setImageError] = useState(false)
  const [isFavorited, setIsFavorited] = useState(false)

  // 检查收藏状态
  const { data: favoriteStatus } = useQuery({
    queryKey: ['favorite-status', 'image', image.id],
    queryFn: () => checkFavoriteStatus('image', image.id)
  })

  // 使用useEffect来更新收藏状态
  useEffect(() => {
    if (favoriteStatus) {
      setIsFavorited(favoriteStatus.is_favorited)
    }
  }, [favoriteStatus])

  // 收藏操作mutation
  const favoriteMutation = useMutation({
    mutationFn: async () => {
      if (isFavorited) {
        return await removeImageFavorite(image.id)
      } else {
        return await addImageFavorite(
          image.id,
          workTitle,
          workId,
          episodeTitle,
          episodeId,
          `/api/images/${image.id}.jpg`, // 小图代理URL
          `/api/images/${image.id}.jpg` // 大图代理URL（避免CSDI链接）
        )
      }
    },
    onSuccess: () => {
      setIsFavorited(!isFavorited)
      queryClient.invalidateQueries({ queryKey: ['favorites'] })
      queryClient.invalidateQueries({ queryKey: ['favorite-status', 'image', image.id] })
    }
  })

  const handleSelectChange = () => {
    onSelect(image.id, !isSelected)
  }

  const handleImageLoad = () => {
    setImageLoaded(true)
    console.log(`✅ 图片加载成功: ${image.id}`)
  }

  const handleImageError = () => {
    setImageError(true)
    setImageLoaded(true)
    const imageUrl = getImageUrl(image)
    console.error(`❌ 图片加载失败: ${image.id}, URL: ${imageUrl}`)
  }

  const handleFavoriteClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    favoriteMutation.mutate()
  }

  return (
    <div className="group relative bg-white rounded-lg border border-gray-200 overflow-hidden hover:border-blue-300 hover:shadow-lg transition-all">
      {/* 图片容器 */}
      <div className="aspect-video relative overflow-hidden bg-gray-100">
        {!imageLoaded && (
          <div className="absolute inset-0 bg-gray-200 animate-pulse flex items-center justify-center">
            <Image className="w-8 h-8 text-gray-400" />
          </div>
        )}
        
        {!imageError ? (
          <AuthenticatedImage
            src={getImageUrl(image)}
            alt={`Screenshot ${image.id}`}
            className={`w-full h-full object-cover transition-transform duration-300 group-hover:scale-105 ${
              imageLoaded ? 'opacity-100' : 'opacity-0'
            }`}
            loading={index < 20 ? 'eager' : 'lazy'} // 前20张立即加载
            onLoad={handleImageLoad}
            onError={handleImageError}
          />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-red-100 to-red-200 flex items-center justify-center">
            <AlertCircle className="w-8 h-8 text-red-400" />
          </div>
        )}

        {/* 选择覆盖层 */}
        <div className={`absolute inset-0 transition-all ${
          isSelected 
            ? 'bg-blue-600 bg-opacity-30' 
            : 'bg-black bg-opacity-0 group-hover:bg-opacity-20'
        }`}>
          {/* 勾选按钮 - 左上角 */}
          <div className="absolute top-2 left-2 z-20">
            <button
              onClick={(e) => {
                e.stopPropagation() // 阻止事件冒泡
                handleSelectChange()
              }}
              className={`p-1 rounded transition-all ${
                isSelected 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-white bg-opacity-80 text-gray-600 hover:bg-opacity-100'
              }`}
            >
              {isSelected ? (
                <CheckSquare className="w-5 h-5" />
              ) : (
                <Square className="w-5 h-5" />
              )}
            </button>
          </div>

          {/* 收藏按钮 - 右上角 */}
          <div className="absolute top-2 right-2 z-20">
            <button
              onClick={handleFavoriteClick}
              disabled={favoriteMutation.isPending}
              className={`p-1 rounded transition-all ${
                isFavorited 
                  ? 'bg-red-500 text-white' 
                  : 'bg-white bg-opacity-80 text-gray-600 hover:bg-opacity-100 hover:text-red-500'
              } ${favoriteMutation.isPending ? 'opacity-50 cursor-not-allowed' : ''}`}
              title={isFavorited ? '取消收藏' : '添加到收藏'}
            >
              <Heart className={`w-5 h-5 ${isFavorited ? 'fill-current' : ''}`} />
            </button>
          </div>

          {/* 预览按钮 - 避开左上角和右上角区域 */}
          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-10">
            <button 
              onClick={(e) => {
                e.stopPropagation() // 阻止事件冒泡，避免触发选择
                window.open(getImageUrl(image), '_blank') // 在新标签页打开大图
              }}
              className="bg-white bg-opacity-90 text-gray-800 rounded-full p-3 hover:bg-white transition-all"
            >
              <Eye className="w-6 h-6" />
            </button>
          </div>
        </div>
      </div>

      {/* 图片信息 */}
      <div className="p-3">
        <p className="text-xs text-gray-600 font-mono">
          ID: {image.id}
        </p>
      </div>
    </div>
  )
}

export default ImagesPage